import {
  VStack,
  Card,
  CardBody,
  Text,
  HStack,
  Badge,
  Box,
  Divider,
  Spinner,
  Icon,
} from '@chakra-ui/react'
import { MdAccessTime, MdImage } from 'react-icons/md'
import { LuBuilding2 } from 'react-icons/lu'
import {
  useLabMalariaAnalyses,
  MalariaAnalysisCase,
} from '../../../api/api_hooks/lab/malariaAnalyses'

interface CasesListProps {
  specialtyId: string
  onCaseSelect?: (caseItem: MalariaAnalysisCase) => void
  selectedCaseId?: string | null
}

export default function CasesList({
  specialtyId,
  onCaseSelect,
  selectedCaseId,
}: CasesListProps) {
  const { data: cases, isLoading, error } = useLabMalariaAnalyses()

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
  }

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'green'
      case 'inProcess':
        return 'blue'
      case 'failed':
        return 'red'
      default:
        return 'gray'
    }
  }

  // Only show malaria data for parasitology specialty
  if (specialtyId !== 'parasitology') {
    return (
      <VStack spacing={4} align="stretch">
        <Text fontSize="sm" color="gray.600">
          Recent analysis cases in {specialtyId}
        </Text>
        <Box textAlign="center" py={8} color="gray.500">
          <Text>No cases available for this specialty yet</Text>
        </Box>
      </VStack>
    )
  }

  if (isLoading) {
    return (
      <Box textAlign="center" py={8}>
        <Spinner color="purple.500" />
      </Box>
    )
  }

  if (error) {
    return (
      <Box textAlign="center" py={8} color="red.500">
        <Text>Error loading cases</Text>
      </Box>
    )
  }

  return (
    <VStack spacing={4} align="stretch">
      {cases && cases.length > 0 ? (
        cases.map((caseItem) => {
          const isSelected = selectedCaseId === caseItem.id

          return (
            <Card
              key={caseItem.id}
              cursor={onCaseSelect ? 'pointer' : 'default'}
              transition="all 0.2s"
              _hover={{
                shadow: onCaseSelect ? 'md' : 'none',
                borderColor: onCaseSelect ? 'purple.300' : 'gray.200',
              }}
              borderWidth={isSelected ? 2 : 1}
              borderColor={isSelected ? 'purple.500' : 'gray.200'}
              shadow={isSelected ? 'md' : 'sm'}
              bg={isSelected ? 'purple.50' : 'white'}
              onClick={() => onCaseSelect?.(caseItem)}
            >
              <CardBody>
                <VStack align="stretch" spacing={3}>
                  {/* Header */}
                  <HStack justify="space-between">
                    <HStack spacing={2}>
                      <MdAccessTime style={{ color: 'var(--chakra-colors-gray-500)' }} />
                      <Text fontSize="sm" fontWeight="semibold">
                        {formatDate(caseItem.date)} at {formatTime(caseItem.date)}
                      </Text>
                    </HStack>
                    <Badge colorScheme={getStatusColor(caseItem.status)}>{caseItem.status}</Badge>
                  </HStack>

                  <Divider />

                  {/* Slide ID */}
                  <HStack>
                    <Text fontSize="xs" fontWeight="semibold" color="purple.700">
                      Slide ID:
                    </Text>
                    <Text fontSize="sm" fontFamily="mono" color="purple.900" fontWeight="bold">
                      {caseItem.slideId}
                    </Text>
                  </HStack>

                  {/* Content */}
                  <HStack>
                    <MdImage style={{ color: 'var(--chakra-colors-purple-500)' }} />
                    <Text fontSize="sm" color="gray.600">
                      {caseItem.imageCount} image{caseItem.imageCount > 1 ? 's' : ''} analyzed
                    </Text>
                  </HStack>

                  <HStack justify="space-between" align="center">
                    <Text fontSize="sm" color="gray.700">
                      {caseItem.summary}
                    </Text>
                    {caseItem.detectionProfile && (
                      <HStack spacing={1} flexShrink={0}>
                        <Icon as={LuBuilding2} boxSize={3} color="purple.500" />
                        <Badge colorScheme="purple" fontSize="2xs" variant="subtle">
                          {caseItem.detectionProfile.toUpperCase()}
                        </Badge>
                      </HStack>
                    )}
                  </HStack>
                </VStack>
              </CardBody>
            </Card>
          )
        })
      ) : (
        <Box textAlign="center" py={8} color="gray.500">
          <Text>No recent cases found</Text>
          <Text fontSize="sm" mt={2}>
            Start analyzing blood smears to see them here
          </Text>
        </Box>
      )}
    </VStack>
  )
}
